import { TCoverageSideEffectsType } from '@sureapp/platform-sdk';

import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';

import { AUTO_DISCOUNTS } from '../../autoDiscounts';
import { featureFlag } from '../../featureFlags';

function ToggleCOConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return isFeatureEnabled(featureFlag.IS_CO_EXPANSION_ENABLED)
    ? {
        autoDiscounts: [
          AUTO_DISCOUNTS.affinity,
          AUTO_DISCOUNTS.matureDriver55,
          AUTO_DISCOUNTS.safeDriving,
          AUTO_DISCOUNTS.multiCar,
          AUTO_DISCOUNTS.multiPolicy,
          AUTO_DISCOUNTS.homeowner,
          AUTO_DISCOUNTS.goodStudent,
          AUTO_DISCOUNTS.distantStudent,
          AUTO_DISCOUNTS.teenDriver,
        ],
        matureDriver: () => ({
          getSubjectText: null,
          label:
            'Has this person completed a mature driver motor vehicle accident prevention course?',
          explanation:
            'Approved defensive driving courses designed for mature drivers help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
        }),
        coverage: (coverage: TConfig['coverage']) => ({
          ...coverage,
          uninsuredMotoristPropertyDamageDeductible: {
            title: 'Uninsured/Underinsured Motorist Property Damage',
            description: `Your bumper sticker might say 'don't hit me if you're uninsured' but it isn't actually protecting you against that. Don't worry, this coverage can help.`,
            modalContent:
              'Uninsured/Underinsured Motorist Property Damage (UMPD) pays, subject to the deductible chosen, the amount you are legally entitled to recover for physical damages to your insured car sustained in an accident caused by a driver who is not insured.',
          },
          uninsuredMotoristBodilyInjury: {
            title: 'Uninsured/Underinsured Motorist Bodily Injury',
            description: `That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.`,
            modalContent:
              'Uninsured/Underinsured Motorist Bodily Injury (UMBI) pays the amount you are legally entitled to recover for damages from bodily injury, up to the limit of liability, when an insured person on your policy is hit by an uninsured driver or an unidentified (e.g. hit-and-run) driver.',
          },
        }),
        coverageTextAlerts: {
          UMBI_UPDATE: ({
            isRemoved,
            isAdded,
            isIncreased,
          }: TCoverageSideEffectsType) => {
            if (!(isRemoved || isAdded || isIncreased)) {
              return `We've decreased your Uninsured/Underinsured Motorist Bodily Injury limits to match the changes you've made to your Bodily Injury coverage.`;
            }
          },
          UMPD_DEDUCTIBLE_UPDATE: ({ isRemoved }: TCoverageSideEffectsType) =>
            isRemoved
              ? `We’ve removed your Uninsured/Underinsured Motorist Property Damage coverage because it cannot be purchased with Comprehensive and Collision coverages.`
              : '',
        },
        initialCoverageValues: () => ({
          gap: 'false',
          temporary_ride: 'null',
          side_hustle: 'false',
          new_car_replacement: 'false',
          oem_parts: 'false',
          towing_road_services: 'false',
        }),
        vehicleSideEffectMessages: (
          vehicleSideEffectMessages: TConfig['vehicleSideEffectMessages']
        ) => ({
          ...vehicleSideEffectMessages,
          new_car_replacement: `This coverage is only available if Comprehensive & Collision is also selected and the model year is two (2) years old or less.`,
          gap:
            'This coverage is only available if Comprehensive and Collision are also selected, the vehicle is financed or leased, and the model year is four (4) years old or less.',
        }),
        visibleCoverages: (visibleCoverages: TConfig['visibleCoverages']) => {
          return {
            ...visibleCoverages,
            protectionCoverages: [
              ...visibleCoverages.protectionCoverages.filter(
                ({ key }) =>
                  ![
                    'pet_passenger',
                    'uninsured_motorist_bodily_injury_limit',
                    'underinsured_motorist_bodily_injury_limit',
                    'uninsured_motorist_property_damage_deductible',
                  ].includes(key)
              ),
              {
                label: 'Uninsured/Underinsured Motorist Property Damage',
                key: 'uninsured_motorist_property_damage_deductible',
                overrideValueLabel: 'Deductible',
              },
              {
                label: 'Uninsured/Underinsured Motorist Bodily Injury',
                key: 'uninsured_motorist_bodily_injury_limit',
              },
              visibleCoverages.protectionCoverages.find(
                ({ key }) => key === 'pet_passenger'
              ),
            ],
            vehicleCoverages: [
              ...visibleCoverages.vehicleCoverages.filter(
                ({ key }) =>
                  ![
                    'underinsured_motorist_property_damage_limit',
                    'uninsured_motorist_property_damage_limit',
                    'uninsured_motorist_property_damage_deductible',
                  ].includes(key)
              ),
            ],
          };
        },
        theftFeeConfig: () => ({
          valueText: 'per vehicle',
          modalContent:
            'This is a non-refundable fee per vehicle that we collect for each policy term that funds the Colorado Auto Theft Prevention Authority (CATPA). The fee per vehicle will be charged at the beginning of your policy term or when a new vehicle is added to your policy.',
        }),
      }
    : {};
}
export default ToggleCOConfig;
